import type { Berichtsbeschreibung } from '@crm/kontrakte';

import { rufe } from './client';

export const AUFWAENDE = ['low', 'medium', 'high', 'xhigh', 'max'] as const;
export type Aufwand = (typeof AUFWAENDE)[number];

/** Die Einstellung der KI (KI-05, KI-06, KI-21). Beträge in US-Dollar — so rechnet der Anbieter ab. */
export type KiEinstellung = {
  readonly aktiv: boolean;
  readonly modell: string;
  readonly aufwand: Aufwand;
  readonly hoechstbetragMonat: number;
  readonly preisEingabe: number;
  readonly preisAusgabe: number;
  readonly avBestaetigtAm: string | null;
  readonly avBestaetigtVon: string | null;
  readonly avVermerk: string | null;
};

export type KiFunktion = {
  readonly kennung: string;
  readonly bezeichnung: string;
  readonly anforderungen: readonly string[];
  readonly ausgeliefert: boolean;
  readonly aktiv: boolean;
  readonly hoechstbetragMonat: number | null;
  readonly kostenMonat: number;
  /** SUP-58: die Anweisung der Verwaltung; null heißt, es gilt die Vorgabe. */
  readonly anweisung: string | null;
  /** null: Die Funktion nimmt keine Anweisung an. */
  readonly anweisungVorgabe: string | null;
};

/**
 * Was die Verwaltung sieht. Der Schlüssel selbst kommt nie mit — nur, ob er
 * gesetzt ist und wie die Variable heißt.
 */
export type KiUebersicht = {
  readonly einstellung: KiEinstellung;
  readonly anbieter: string;
  readonly eingerichtet: boolean;
  readonly erwarteteVariablen: readonly string[];
  readonly zustand: { readonly bereit: boolean; readonly hinweis: string };
  readonly kostenMonat: number;
  readonly mandanten: readonly { readonly mandant: string; readonly bezeichnung: string; readonly aktiv: boolean }[];
  readonly rollen: readonly { readonly rolle: string; readonly bezeichnung: string; readonly aktiv: boolean }[];
  readonly funktionen: readonly KiFunktion[];
};

export type KiEinstellungsaenderung = {
  readonly aktiv: boolean;
  readonly modell: string;
  readonly aufwand: Aufwand;
  readonly hoechstbetragMonat: number;
  readonly preisEingabe: number;
  readonly preisAusgabe: number;
  readonly avBestaetigt: boolean;
  readonly avVermerk: string | null;
};

/** Ein Modell, wie der Anbieter es beschreibt, und ob der Modellzugang es bedienen kann (KI-21). */
export type KiModell = {
  readonly kennung: string;
  readonly name: string;
  readonly erschienenAm: string | null;
  readonly kontextToken: number | null;
  readonly ausgabeToken: number | null;
  /** null: Der Anbieter nennt die Fähigkeiten nicht. */
  readonly bedienbar: boolean | null;
  readonly fehlt: readonly ('strukturierte_ausgabe' | 'adaptives_denken' | 'aufwand')[];
  readonly aufwaende: readonly Aufwand[];
};

/** Die Modelle zur Wahl. Ohne Liste ist `modelle` leer und `grund` sagt warum — geraten wird nichts. */
export type KiModellwahl = {
  readonly eingestellt: string;
  /** Das neueste zuerst. */
  readonly modelle: readonly KiModell[];
  readonly grund: string | null;
  readonly abgefragtAm: string | null;
};

export type KiVerbindungstest = {
  readonly ergebnis: 'antwort' | 'abgelehnt' | 'unbrauchbar';
  readonly angefragtesModell: string;
  readonly modell: string;
  readonly dauerMs: number;
  readonly kosten: number;
};

/** KI-08: je Funktion und Kalendermonat. */
export type KiAuswertung = {
  readonly qualitaet: readonly {
    readonly funktion: string;
    readonly monat: string;
    readonly uebernommen: number;
    readonly geaendert: number;
    readonly verworfen: number;
    readonly offen: number;
    readonly anteilUebernommen: number | null;
    readonly anteilGeaendert: number | null;
    readonly anteilVerworfen: number | null;
  }[];
  readonly kosten: readonly {
    readonly funktion: string;
    readonly monat: string;
    readonly aufrufe: number;
    readonly eingabeToken: number;
    readonly ausgabeToken: number;
    readonly kosten: number;
  }[];
};

/** Auftrag CRM-47: Tokens nach Art — Eingabe, Ausgabe und der Cache in beide Richtungen. */
export type KiTokens = {
  readonly eingabe: number;
  readonly ausgabe: number;
  readonly cacheGelesen: number;
  readonly cacheGeschrieben: number;
};

export type KiNutzungswerte = { readonly anfragen: number; readonly tokens: KiTokens; readonly kosten: number };

/** Verbraucht gegen einen Höchstbetrag: gelb ab 80 % (`knapp`), rot ab 95 % (`erschoepft`). */
export type KiBudget = {
  readonly hoechstbetrag: number;
  readonly verbraucht: number;
  readonly rest: number;
  readonly anteil: number;
  readonly stufe: 'im_rahmen' | 'knapp' | 'erschoepft';
};

/**
 * Auftrag CRM-47: Nutzung und Budget unter Umsysteme › KI. Gerechnet vom
 * CRM aus den Token-Angaben der Antworten und den eingestellten Preisen —
 * maßgeblich für die Rechnung ist die Konsole des Anbieters.
 */
export type KiNutzung = {
  readonly monat: string;
  readonly tag: number;
  readonly tageImMonat: number;
  readonly budget: KiBudget & { readonly hochrechnung: number };
  readonly gesamt: KiNutzungswerte;
  readonly funktionen: readonly (KiNutzungswerte & {
    readonly kennung: string;
    readonly bezeichnung: string;
    readonly eigenerHoechstbetrag: boolean;
    readonly budget: KiBudget;
  })[];
  readonly modelle: readonly (KiNutzungswerte & { readonly modell: string })[];
  readonly verlauf: readonly (KiNutzungswerte & { readonly monat: string })[];
};

export async function kiNutzung(): Promise<KiNutzung> {
  return rufe('/ki/nutzung');
}

export async function kiUebersicht(signal?: AbortSignal): Promise<KiUebersicht> {
  return rufe('/ki/einstellung', signal === undefined ? {} : { signal });
}

export async function kiEinstellungAendern(aenderung: KiEinstellungsaenderung): Promise<KiUebersicht> {
  return rufe('/ki/einstellung', { verfahren: 'PUT', rumpf: aenderung });
}

export async function kiModelle(): Promise<KiModellwahl> {
  return rufe('/ki/modelle');
}

export async function kiMandantSetzen(mandant: string, aktiv: boolean): Promise<KiUebersicht> {
  return rufe(`/ki/mandanten/${encodeURIComponent(mandant)}`, { verfahren: 'PUT', rumpf: { aktiv } });
}

export async function kiRolleSetzen(rolle: string, aktiv: boolean): Promise<KiUebersicht> {
  return rufe(`/ki/rollen/${encodeURIComponent(rolle)}`, { verfahren: 'PUT', rumpf: { aktiv } });
}

export async function kiFunktionSetzen(
  kennung: string,
  aktiv: boolean,
  hoechstbetragMonat: number | null,
): Promise<KiUebersicht> {
  return rufe(`/ki/funktionen/${encodeURIComponent(kennung)}`, {
    verfahren: 'PUT',
    rumpf: { aktiv, hoechstbetragMonat },
  });
}

/** SUP-58: null setzt auf die Vorgabe zurück. */
export async function kiAnweisungSetzen(kennung: string, anweisung: string | null): Promise<KiUebersicht> {
  return rufe(`/ki/funktionen/${encodeURIComponent(kennung)}/anweisung`, { verfahren: 'PUT', rumpf: { anweisung } });
}

export async function kiVerbindungstest(): Promise<KiVerbindungstest> {
  return rufe('/ki/verbindungstest', { verfahren: 'POST' });
}

export async function kiAuswertung(monate = 12, signal?: AbortSignal): Promise<KiAuswertung> {
  return rufe(`/ki/auswertung?monate=${monate}`, signal === undefined ? {} : { signal });
}

// ---------------------------------------------------------------------------
// Die KI in den Berichten (KI-15, Auftrag #54)
// ---------------------------------------------------------------------------

/** KI-07: ob eine Funktion diesem Anwender gerade zur Verfügung steht — und wenn nicht, warum. */
export type KiStand = readonly {
  readonly funktion: string;
  readonly bezeichnung: string;
  readonly verfuegbarkeit: { readonly verfuegbar: true } | { readonly verfuegbar: false; readonly hinweis: string };
}[];

export async function kiStand(): Promise<KiStand> {
  return rufe('/ki/stand');
}

/** KI-01: Jede KI-Ausgabe trägt Zeitpunkt, Modell und Datengrundlage. */
export type KiKennzeichnung = {
  readonly ki: true;
  readonly erzeugtAm: string;
  readonly modell: string;
  readonly datengrundlage: string;
};

/** Ein KI-Vorschlag — Entwurf, bis der Anwender entscheidet (KI-02, KI-04). */
export type KiVorschlag<T> = {
  readonly id: string;
  readonly funktion: string;
  readonly kennzeichnung: KiKennzeichnung;
  /** Für wen er entstand — nur der entscheidet über ihn. */
  readonly erzeugtFuer?: string;
  readonly quellen: readonly { readonly art: string; readonly id: string; readonly bezeichnung: string }[];
  readonly entwurf: T;
  readonly entscheidung: 'uebernommen' | 'geaendert' | 'verworfen' | null;
  readonly fassung: T | null;
};

/** „In eigenen Worten": die Beschreibung des Baukastens, keine Zahl. */
export type Berichtsentwurf = {
  readonly frage: string;
  readonly grundlage: Berichtsbeschreibung | null;
  /** null: Der Katalog kann die Frage nicht beantworten — `hinweise` sagt warum. */
  readonly beschreibung: Berichtsbeschreibung | null;
  readonly titel: string;
  readonly hinweise: readonly string[];
};

export type Erlaeuterungsaussage = {
  readonly text: string;
  readonly kennzahl: string;
  readonly kennzahlName: string;
  /** null ist die Gesamtzeile. */
  readonly zeile: string | null;
};

export type Erlaeuterungsentwurf = {
  readonly aussagen: readonly Erlaeuterungsaussage[];
  readonly bericht: Berichtsbeschreibung;
  readonly zeitraum: { readonly von: string; readonly bis: string };
  readonly stand: string;
};

/**
 * Auftrag CRM-47: Die Anzeige gehoert nicht zum Vorschlag. „In eigenen
 * Worten" laesst sie, wie sie ist — die KI bekommt sie nicht zu sehen, und
 * eine Uebernahme zaehlt nicht als Aenderung, nur weil die eigene Anzeige
 * mitgeht (KI-08).
 */
function ohneAnzeige(b: Berichtsbeschreibung): Berichtsbeschreibung {
  const kopie = { ...b };
  delete kopie.anzeige;
  return kopie;
}

export async function berichtVorschlagen(
  frage: string,
  grundlage: Berichtsbeschreibung | null,
  vorgaenger: string | null,
): Promise<KiVorschlag<Berichtsentwurf>> {
  return rufe('/ki/berichte/vorschlag', {
    verfahren: 'POST',
    rumpf: { frage, grundlage: grundlage === null ? null : ohneAnzeige(grundlage), vorgaenger },
  });
}

export async function berichtsvorschlagEntscheiden(
  id: string,
  entscheidung: { readonly art: 'uebernehmen'; readonly beschreibung: Berichtsbeschreibung } | { readonly art: 'verwerfen' },
): Promise<KiVorschlag<Berichtsentwurf>> {
  return rufe(`/ki/berichte/vorschlag/${encodeURIComponent(id)}/entscheidung`, {
    verfahren: 'POST',
    rumpf: entscheidung.art === 'uebernehmen' ? { ...entscheidung, beschreibung: ohneAnzeige(entscheidung.beschreibung) } : entscheidung,
  });
}

export async function erlaeuterungErzeugen(beschreibung: Berichtsbeschreibung): Promise<KiVorschlag<Erlaeuterungsentwurf>> {
  return rufe('/ki/berichte/erlaeuterung', { verfahren: 'POST', rumpf: { beschreibung } });
}

export async function erlaeuterungEntscheiden(
  id: string,
  entscheidung:
    | {
        readonly art: 'uebernehmen';
        readonly aussagen: readonly { readonly text: string; readonly kennzahl: string; readonly zeile: string | null }[];
      }
    | { readonly art: 'verwerfen' },
): Promise<KiVorschlag<Erlaeuterungsentwurf>> {
  return rufe(`/ki/berichte/erlaeuterung/${encodeURIComponent(id)}/entscheidung`, { verfahren: 'POST', rumpf: entscheidung });
}

// ---------------------------------------------------------------------------
// Der KI-Entwurf für Rezensionsantworten (SUP-56 bis SUP-59, Auftrag CRM-84)
// ---------------------------------------------------------------------------

export const FUNKTION_REZENSIONSANTWORT = 'rezensionsantwort';

export type Rezensionsentwurf = {
  readonly rezension: string;
  readonly text: string;
};

export async function rezensionsentwurfErzeugen(rezension: string): Promise<KiVorschlag<Rezensionsentwurf>> {
  return rufe(`/ki/rezensionen/${encodeURIComponent(rezension)}/entwurf`, { verfahren: 'POST' });
}

/** KI-04: übernommen heißt hier „mit diesem Text gesendet“ — weicht er ab, zählt es als geändert (KI-08). */
export async function rezensionsentwurfEntscheiden(
  id: string,
  entscheidung: { readonly art: 'uebernehmen'; readonly text: string } | { readonly art: 'verwerfen' },
): Promise<KiVorschlag<Rezensionsentwurf>> {
  return rufe(`/ki/rezensionen/entwurf/${encodeURIComponent(id)}/entscheidung`, { verfahren: 'POST', rumpf: entscheidung });
}
